import {
    GlobalConfigProperties,
    getConfig,
} from '../api/ABHub/ABHubGlobalConfig';
import {
    isSreHealthAvailable,
    SRE_VALID_CLOUDS,
    SRE_VALID_ENVIRONMENTS,
} from '../util/sreClouds';

export interface SreAccess {
    /**
     * Whether the SRE endpoints should be listed for this cloud/environment. This deliberately
     * ignores whether the AGRM URL is configured, matching how the admin card list has always
     * decided visibility.
     */
    hasAccess: boolean;
    /** Whether an AGRM URL is configured, so the endpoints can actually be called. */
    hasAgrmUrl: boolean;
    /** hasAccess and hasAgrmUrl together: safe to issue a request. */
    canCallApi: boolean;
    cloud: string;
    environment: string;
    agrmUrl: string;
    /** Whether this is a dev/test environment rather than a sovereign cloud. */
    isDevEnvironment: boolean;
    /** Populated only when canCallApi is false, for display in a MessageBar. */
    reason?: string;
}

/**
 * Resolves whether the current cloud/environment can use the SRE endpoints.
 *
 * Consolidates the copies that had drifted across AdminEndpoints, the two SRE cards and the AGRM
 * API layer. The cloud list now lives in Common/util/sreClouds and AGRMApi delegates to it.
 */
export const useSreAccess = (): SreAccess => {
    const cloud = getConfig(GlobalConfigProperties.Cloud);
    const environment = getConfig(GlobalConfigProperties.Environment);
    const agrmUrl = getConfig(GlobalConfigProperties.AgrmUrl);

    const isDevEnvironment = SRE_VALID_ENVIRONMENTS.includes(environment);
    const hasAccess = isSreHealthAvailable(cloud, environment);

    const hasAgrmUrl = Boolean(agrmUrl?.trim());
    const canCallApi = hasAccess && hasAgrmUrl;

    let reason: string | undefined;
    if (!hasAccess) {
        reason = `SRE endpoints are only available in sovereign clouds (${SRE_VALID_CLOUDS.join(
            ', '
        )}), sovmock, or dev/test environments. Current cloud: ${
            cloud || 'Unknown'
        }`;
    } else if (!hasAgrmUrl) {
        reason = `No SRE (AGRM) URL is configured for ${
            environment || 'this environment'
        }, so these endpoints cannot be called here.`;
    }

    return {
        hasAccess,
        hasAgrmUrl,
        canCallApi,
        cloud,
        environment,
        agrmUrl,
        isDevEnvironment,
        reason,
    };
};
